import React from 'react';
import {List, Map} from 'immutable';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import SelectField from 'components/pure/utils/form/SelectField';
import TextAreaField from 'components/pure/utils/form/TextAreaField';
import './AdHocCoachingDialog.styl';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import {UserSelector} from 'components/pure/utils/form/UserSelector';
import AuthStore from 'stores/AuthStore';
import ReviewTypeDropdown from 'components/dashboard/common/ReviewTypeDropdown';
import SelectizeLookingDropdownArrow from 'components/utils/SelectizeLookingDropdownArrow';
import CoachingContentDetail from 'records/CoachingContentDetail';
import User from 'records/User';
import CoachingReviewType, {CoachingReviewTypeId} from 'records/CoachingReviewType';
import {IdMap} from '@store/@types/util';
import Skill from 'records/Skill';

interface AdhocFormProps {
  contentDetail: CoachingContentDetail;
  skills: List<Skill>;
  reviewTypes: Map<CoachingReviewTypeId, CoachingReviewType>;
  users: IdMap<User>;
  rep?: User;
  reviewType?: CoachingReviewType;

  isSubmitting: boolean;
  noCallOnly: boolean;
  includesCallsOnly: boolean;
  showRepSelect: boolean;
  showCustom: boolean;

  setRep: (rep: User) => void;
  onChange: () => void;
  toggleSkillType: () => void;
  onChangeReviewType: () => void;
}

export class AdhocForm extends React.PureComponent<AdhocFormProps> {
  inputRef = null;

  render() {
    const {
      contentDetail: detail,
      rep,
      skills,
      onChange,
      isSubmitting,
      reviewType,
      reviewTypes,
      onChangeReviewType,
      noCallOnly,
      includesCallsOnly,
      showRepSelect,
      setRep,
      users,
      showCustom,
      toggleSkillType,
    } = this.props;

    const sessionUser = AuthStore.getState().user;

    return (
      <div className="adhoc-form">
        <div>
          <DialogPrompt rep={rep} />
          {showRepSelect && (
            <FieldContainer label="Rep">
              <UserSelector
                // Don't allow user to set rep as themselves
                users={users.delete(sessionUser.id)}
                value={rep?.id}
                onChange={id => setRep(users.find(u => u.id === id))}
              />
            </FieldContainer>
          )}
          <ReviewTypeSelector
            reviewTypeId={reviewType?.id}
            reviewTypes={reviewTypes}
            noCallOnly={noCallOnly}
            includesCallsOnly={includesCallsOnly}
            onChange={onChangeReviewType}
          />
          <ContentDetailSelector
            detail={detail}
            onChange={onChange}
            toggleSkillType={toggleSkillType}
            skills={skills}
            showCustom={showCustom}
            setRef={ref => (this.inputRef = ref)}
          />
        </div>
        {isSubmitting && <LoadingOverlay />}
      </div>
    );
  }
}

function DialogPrompt({rep}) {
  return (
    <p>
      To start a one-off coaching session{rep && ' with ' + rep.getFullName()}, select the skill
      that you'll be coaching and a meeting type then click Next to start the meeting.
    </p>
  );
}

function ContentDetailSelector({detail, onChange, toggleSkillType, skills, showCustom, setRef}) {
  const skillOptions = skills.map(s => ({
    label: s.skill,
    value: s.id,
  }));
  const skillMap = Map(skills.map(s => [s.id, s.skill]));

  return showCustom ? (
    <div>
      <FieldContainer label={'Custom Skill'} labelType="div">
        <TextAreaField
          className={'has-label'}
          value={detail.custom || ''}
          onChange={val => onChange([val])}
          rows="3"
          placeholder="Describe what will be coached during this session"
          inputRef={setRef}
        />
      </FieldContainer>
      <span className="generic-link pointer" onClick={toggleSkillType}>
        Choose standard skill instead
      </span>
    </div>
  ) : (
    <div>
      <FieldContainer label={'Skill'} labelType="div">
        <SelectField
          value={detail.skillId}
          options={skillOptions}
          tether={true}
          onChange={id => onChange([skillMap.get(id), id])}
        />
      </FieldContainer>
      <span className="generic-link pointer" onClick={toggleSkillType}>
        Coach something else
      </span>
    </div>
  );
}

interface ReviewTypeSelectorProps {
  reviewTypeId: CoachingReviewTypeId | undefined;
  reviewTypes: Map<CoachingReviewTypeId, CoachingReviewType>;
  onChange: () => void;
  noCallOnly: boolean;
  includesCallsOnly: boolean;
}

function ReviewTypeSelector({
  reviewTypeId,
  reviewTypes: reviewTypesIncoming = Map(),
  onChange,
  noCallOnly,
  includesCallsOnly,
}: ReviewTypeSelectorProps) {
  const reviewTypes = reviewTypesIncoming.filter(type => {
    if (!noCallOnly && !includesCallsOnly) {
      return true;
    } else if (noCallOnly) {
      return !type.includesCalls;
      // includes calls only
    } else {
      return type.includesCalls;
    }
  });

  return (
    <FieldContainer label={'Meeting Type'} labelType="div">
      <ReviewTypeDropdown
        dropdownHeight={150}
        value={reviewTypeId}
        coachingReviewTypes={reviewTypes.toList()}
        onSelect={onChange}
        width={295}
        arrow={(open, onShowDropdown) => (
          <SelectizeLookingDropdownArrow open={open} onShowDropdown={onShowDropdown} />
        )}
      />
    </FieldContainer>
  );
}
